import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

// SUNSKY-approved destination airports (build order, step 11): one main + up to three alternatives.
const BASE = '/geo/destination-airports';

export const getDestinationAirports = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

/**
 * destination: the GeoData destination ID (or, for older rows, its code).
 * airports: [{ airportCode, isMain }] — max 4, exactly one main; [] = Airport Mapping Missing;
 *   undefined = leave them as they are. A removed airport is deactivated, not deleted.
 * longHaul / flightHotelEnabled (optional): left out = unchanged.
 */
export const saveDestinationAirports = (destination, airports, { longHaul, flightHotelEnabled } = {}) =>
  axiosInstance.put(`${BASE}/${encodeURIComponent(destination)}`, {
    ...(airports !== undefined ? { airports } : {}),
    ...(typeof longHaul === "boolean" ? { longHaul } : {}),
    ...(typeof flightHotelEnabled === "boolean" ? { flightHotelEnabled } : {}),
  }).then((res) => res.data);

/** "Sync from GeoData": newly activated destinations appear as Airport Mapping Missing. */
export const syncDestinationAirports = () => axiosInstance.post(`${BASE}/sync`).then((res) => res.data);

/** { data, maxAirports, loading, error, refetch } for the destination list. */
export const useDestinationAirports = ({ search = '', needsReview = false } = {}) => {
  const [data, setData] = useState([]);
  const [maxAirports, setMaxAirports] = useState(4);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDestinationAirports({ search: search || undefined, needsReview: needsReview ? 1 : undefined });
      setData(res.data || []);
      setMaxAirports(res.maxAirports || 4);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [search, needsReview]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, maxAirports, loading, error, refetch };
};
